/**
 * Analytics utility module for Google Analytics 4 (GA4) event tracking.
 *
 * All tracking functions are no-ops when:
 *  - Running on the server (SSR/SSG)
 *  - The gtag global is not loaded (non-production builds)
 *
 * Events mirror the tracking defined in the legacy new-docupro.in site.
 */

type GtagCommand = 'config' | 'event' | 'js' | 'set';

interface WindowWithGtag extends Window {
  gtag?: (command: GtagCommand, ...args: unknown[]) => void;
  dataLayer?: Record<string, unknown>[];
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function getGtag(): WindowWithGtag['gtag'] | undefined {
  if (typeof window === 'undefined') return undefined;
  return (window as WindowWithGtag).gtag;
}

// ---------------------------------------------------------------------------
// Lead form events  (matches new-docupro.in/app/lib/analytics.ts)
// ---------------------------------------------------------------------------

/**
 * Fire when the lead-capture modal becomes visible.
 *
 * GA4 event: `lead_form_view`
 */
export function trackLeadFormView(pageUrl: string): void {
  const gtag = getGtag();
  if (!gtag) return;

  gtag('event', 'lead_form_view', {
    page_location: pageUrl,
    custom_map: {
      custom_parameter_1: 'lead_form_opened',
    },
  });
}

/**
 * Fire after a lead form is successfully submitted.
 *
 * GA4 event: `generate_lead`  (GA4 recommended event name)
 */
export function trackLeadSubmission(leadData: {
  service: string;
  source: string;
}): void {
  const gtag = getGtag();
  if (!gtag) return;

  gtag('event', 'generate_lead', {
    currency: 'INR',
    value: 1.0,
    service: leadData.service,
    source: leadData.source,
  });
}

// ---------------------------------------------------------------------------
// Account events
// ---------------------------------------------------------------------------

/**
 * Fire after an account deletion request is successfully submitted.
 *
 * GA4 event: `account_deletion_requested`
 * (The buttons themselves carry ids `delete_account` / `delete_account_submit`
 * for GTM click triggers.)
 */
export function trackAccountDeletionRequested(): void {
  const gtag = getGtag();
  if (!gtag) return;

  gtag('event', 'account_deletion_requested');
}
